import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Badge, Button, Container, Row, Col, Spinner, Form, Tooltip, OverlayTrigger, ButtonGroup, Dropdown } from 'react-bootstrap'
import { inventoryItemAPI } from '../api/inventoryItemAPI'
import { vendorAPI } from '../api/vendorAPI'
import { warehouseAPI } from '../api/warehouseAPI'
import { locationAPI } from '../api/locationAPI'
import { stockItemCategoryAPI } from '../api/stockItemCategoryAPI'
import { brandAPI } from '../api/brandAPI'
import { settingsAPI } from '../api/settingsAPI'
import { calculateVolumeCubicFeet, calculateNetCaseWeight, calculateGrossCaseWeight, highlightText, formatCurrency, formatUnitCost, formatQuantity, parseDateOnly, formatDateOnly, formatDate } from '../utils/commonUtils'
import PaginationComponent from '../components/Pagination'
import StockItemInfo from '../components/StockItemInfo'
import BulkOperationsToolbar from '../components/BulkOperationsToolbar'
import DatePicker from 'react-datepicker'
import "react-datepicker/dist/react-datepicker.css"
import { createColumnToggleDropdown, createFilterDropdown, handleSearchWithDebounce, getCountryFlag } from '../utils/tableUtils'
import 'material-icons/iconfont/material-icons.css';
import ResizableTable from '../components/ResizableTable'
import StickyScrollbar from '../components/StickyScrollbar'
import Select from 'react-select'
import '../styles/selectControl.css'
import SearchInputWithClear from '../components/SearchInputWithClear'
import { usePermissions } from '../contexts/PermissionContext'
import CopyableText from '../components/CopyableText'

// Constant for expiration threshold
const EXPIRATION_WARNING_DAYS = 90;

const UOM_MAP = {
    'bag': 'Bag',
    'bulk': 'Bulk',
    'clear_film': 'Clear Film',
    'ct': 'Count',
    'film': 'Film',
    'jar': 'Jar',
    'pallet': 'Pallet',
    'tub': 'Tub',
    'tote_bag': 'Tote Bag'
};


function InventoryListScreen() {
    const { hasPermission } = usePermissions();
    const [inventoryItems, setInventoryItems] = useState([])
    
    // Helper: returns 'expired', 'expiring_soon', or null
    const getExpirationStatus = (bbDate) => {
        if (!bbDate) return null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const parsed = parseDateOnly(bbDate);
        if (!parsed) return null;
        parsed.setHours(0, 0, 0, 0);
        const diffDays = Math.ceil((parsed.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        if (diffDays < 0) return 'expired';
        if (diffDays <= EXPIRATION_WARNING_DAYS) return 'expiring_soon';
        return null;
    };

    // Helper function to determine row styling based on status and BB date expiration
    const getRowClassName = (row) => {
        const expStatus = getExpirationStatus(row.bb_date);
        if (expStatus === 'expired') return 'table-danger';
        if (expStatus === 'expiring_soon') return 'table-warning';
        if (row.in_production) return 'table-info';
        return '';
    };
    const [loading, setLoading] = useState(true)
    const [vendorMap, setVendorMap] = useState({})
    const [warehouseMap, setWarehouseMap] = useState({})
    const [locationMap, setLocationMap] = useState({})
    const navigate = useNavigate()
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    
    // Initialize all filter states from URL query params if they exist
    const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '')
    const [searchInputValue, setSearchInputValue] = useState(searchParams.get('search') || '') // For immediate UI feedback
    const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1)
    const [selectedVendor, setSelectedVendor] = useState(searchParams.get('vendor') || '')
    const [selectedCategories, setSelectedCategories] = useState(
        searchParams.get('categories') ? searchParams.get('categories').split(',') : []
    )
    const [bbDateRange, setBbDateRange] = useState({
        startDate: searchParams.get('bbStartDate') ? new Date(searchParams.get('bbStartDate')) : null,
        endDate: searchParams.get('bbEndDate') ? new Date(searchParams.get('bbEndDate')) : null
    })
    const [displayedDateRange, setDisplayedDateRange] = useState({
        startDate: searchParams.get('bbStartDate') ? new Date(searchParams.get('bbStartDate')) : null,
        endDate: searchParams.get('bbEndDate') ? new Date(searchParams.get('bbEndDate')) : null
    })
    const [selectedWarehouse, setSelectedWarehouse] = useState(searchParams.get('warehouse') || '')
    const [selectedLocation, setSelectedLocation] = useState(searchParams.get('location') || '')
    const [selectedInTransit, setSelectedInTransit] = useState(searchParams.get('inTransit') || '')
    const [selectedManufacturer, setSelectedManufacturer] = useState(searchParams.get('manufacturer') || '')
    const [selectedBrand, setSelectedBrand] = useState(searchParams.get('brand') || '')
    const [selectedUom, setSelectedUom] = useState(searchParams.get('uom') || '')
    const [pageSize, setPageSize] = useState(parseInt(searchParams.get('pageSize')) || PaginationComponent.getCurrentPageSize())
    
    // State variables for item selection
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    
    // Keep other state variables as they were
    const [shouldFetchData, setShouldFetchData] = useState(0);
    const [totalQuantity, setTotalQuantity] = useState(0);
    const [totalCases, setTotalCases] = useState(0);
    const [totalWeightLbs, setTotalWeightLbs] = useState(0);
    const [totalPallets, setTotalPallets] = useState(0);
    const [totalFilm, setTotalFilm] = useState(0);
    const [totalClearFilm, setTotalClearFilm] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const debounceTimeoutRef = useRef(null);
    const [categoryMap, setCategoryMap] = useState({});
    const [brandMap, setBrandMap] = useState({});

    // Sorting state
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);
    
    const [columnVisibility, setColumnVisibility] = useState(() => {
        const savedVisibility = localStorage.getItem('inventoryItemColumnVisibility');
        return savedVisibility ? JSON.parse(savedVisibility) : {
            selection: true,
            inventory_id: false,
            stock_item_id: false,
            sku: true,
            image: true,
            gtin: true,
            upc: false,
            description: true,
            lot: true,
            notes: true,
            receivedDate: false,
            bbDate: true,
            warehouse: true,
            location: true,
            categories: false,
            manufacturer: false,
            countryOfOrigin: false,
            vendor: true,
            cost: true,
            totalCost: true,
            uom: false,
            itemSize: false,
            casePack: false,
            netCaseWeight: false,
            packageWeight: false,
            grossCaseWeight: false,
            ti: false,
            hi: false,
            length: false,
            width: false,
            height: false,
            volume: false,
            quantity: true,
            in_transit: true,
            brand: false
        };
    });

    const [columnWidths, setColumnWidths] = useState(() => {
        const savedWidths = localStorage.getItem('inventoryItemColumnWidths');
        return savedWidths ? JSON.parse(savedWidths) : {
            actions: 'auto',
            selection: '60px',
            inventory_id: 'auto',
            stock_item_id: 'auto',
            sku: 'auto',
            image: 'auto',
            gtin: 'auto',
            upc: 'auto',
            description: 'auto',
            lot: 'auto',
            notes: 'auto',
            receivedDate: 'auto',
            bbDate: 'auto',
            warehouse: 'auto',
            location: 'auto',
            categories: 'auto',
            manufacturer: 'auto',
            countryOfOrigin: 'auto',
            vendor: 'auto',
            cost: 'auto',
            totalCost: 'auto',
            uom: 'auto',
            itemSize: 'auto',
            casePack: 'auto',
            netCaseWeight: 'auto',
            packageWeight: 'auto',
            grossCaseWeight: 'auto',
            ti: 'auto',
            hi: 'auto',
            length: 'auto',
            width: 'auto',
            height: 'auto',
            volume: 'auto',
            quantity: 'auto',
            in_transit: 'auto',
            brand: 'auto'
        };
    });

    const [systemSettings, setSystemSettings] = useState({
        enable_warehouse_transit: true // default to true for safety
    });

    useEffect(() => {
        localStorage.setItem('inventoryItemColumnVisibility', JSON.stringify(columnVisibility));
    }, [columnVisibility]);

    useEffect(() => {
        localStorage.setItem('inventoryItemColumnWidths', JSON.stringify(columnWidths));
    }, [columnWidths]);

    const columnLabels = {
        selection: 'Select',
        inventory_id: 'Inventory ID',
        stock_item_id: 'Stock Item ID',
        sku: 'SKU',
        image: 'Image',
        gtin: 'GTIN',
        upc: 'UPC',
        description: 'Description',
        lot: 'LOT',
        notes: 'Notes',
        receivedDate: 'Received Date',
        bbDate: 'Best Before',
        warehouse: 'Warehouse',
        location: 'Location',
        categories: 'Categories',
        manufacturer: 'Manufacturer',
        countryOfOrigin: 'Country of Origin',
        vendor: 'Vendor',
        cost: 'Cost',
        totalCost: 'Total Cost',
        uom: 'UOM',
        itemSize: 'Item Size',
        casePack: 'Case Pack',
        netCaseWeight: 'Net Case Weight',
        packageWeight: 'Package Weight',
        grossCaseWeight: 'Gross Case Weight',
        ti: 'TI',
        hi: 'HI',
        length: 'Length (inch)',
        width: 'Width (inch)',
        height: 'Height (inch)',
        volume: 'Volume',
        quantity: 'Quantity',
        ...(systemSettings.enable_warehouse_transit && { in_transit: 'In Transit' }),
        brand: 'Brand'
    };

    useEffect(() => {
        async function fetchInventoryItems() {
            try {
                setLoading(true);

                // Check if we have specific IDs in the URL
                const searchParams = new URLSearchParams(location.search);
                const specificIds = searchParams.get('ids')?.split(',');

                if (specificIds && specificIds.length > 0) {
                    // If we have specific IDs, fetch those items directly
                    const items = await inventoryItemAPI.getByIds(specificIds);
                    setInventoryItems(items);
                    setTotalPages(1); // Only one page when showing specific items
                    setTotalQuantity(items.reduce((sum, item) => sum + (item.quantity || 0), 0));
                    setTotalCases(0); // Reset cases for specific items view
                    setTotalWeightLbs(0); // Reset weight for specific items view
                    setTotalPallets(0); // Reset pallets for specific items view
                    setTotalFilm(0); // Reset film for specific items view
                    setTotalClearFilm(0); // Reset clear film for specific items view
                } else {
                    // Normal fetching with filters
                    const selectedCategoriesString = selectedCategories.map(id => `${id}`).join(',');
                    const response = await inventoryItemAPI.getAll(
                        currentPage,
                        pageSize,
                        searchTerm,
                        selectedVendor,
                        selectedManufacturer,
                        selectedCategoriesString,
                        bbDateRange.startDate?.toISOString().split('T')[0],
                        bbDateRange.endDate?.toISOString().split('T')[0],
                        null, // receivedStartDate
                        null, // receivedEndDate
                        selectedWarehouse,
                        selectedLocation,
                        selectedInTransit,
                        sortColumn,
                        sortDirection,
                        selectedBrand || undefined,
                        selectedUom || undefined
                    );
                    const data = response.results.results;
                    setTotalQuantity(response.results.total_quantity || 0);
                    setTotalCases(response.results.total_cases || 0);
                    setTotalWeightLbs(response.results.total_weight_lbs || 0);
                    setTotalPallets(response.results.total_pallets || 0);
                    setTotalFilm(response.results.total_film || 0);
                    setTotalClearFilm(response.results.total_clear_film || 0);
                    
                    if (!Array.isArray(data)) {
                        throw new Error('Expected an array of inventory items');
                    }
                    
                    setInventoryItems(data);
                    setTotalPages(response.count);
                }
            } catch (error) {
                console.error('Error fetching inventory items:', error);
            } finally {
                setLoading(false);
            }
        }
        fetchInventoryItems();
    }, [currentPage,
        searchTerm,
        selectedVendor,
        selectedManufacturer,
        selectedBrand,
        selectedUom,
        selectedCategories,
        selectedWarehouse,
        selectedLocation,
        shouldFetchData,
        selectedInTransit,
        pageSize,
        location.search
    ]);

    useEffect(() => {
        async function fetchVendors() {
            try {
                const vendors = await vendorAPI.getAll()
                const mapping = vendors.reduce((acc, vendor) => {
                    acc[vendor._id] = vendor.name
                    return acc
                }, {})
                setVendorMap(mapping)
            } catch (error) {
                console.error('Error fetching vendors:', error)
            }
        }
        fetchVendors()
    }, [])

    useEffect(() => {
        async function fetchWarehouses() {
            try {
                const warehouses = await warehouseAPI.getAll()
                const mapping = warehouses.reduce((acc, warehouse) => {
                    acc[warehouse._id] = warehouse.name
                    return acc
                }, {})
                setWarehouseMap(mapping)
            } catch (error) {
                console.error('Error fetching warehouses:', error)
            }
        }
        fetchWarehouses()
    }, [])

    useEffect(() => {
        async function fetchLocations() {
            try {
                const locations = await locationAPI.getAll(selectedWarehouse)
                const mapping = locations.reduce((acc, location) => {
                    acc[location._id] = location.name
                    return acc
                }, {})
                setLocationMap(mapping)
            } catch (error) {
                console.error('Error fetching locations:', error)
            }
        }

        if (selectedWarehouse) {
            fetchLocations()
        } else {
            setLocationMap({})
        }
    }, [selectedWarehouse])

    useEffect(() => {
        async function fetchCategories() {
            try {
                const categories = await stockItemCategoryAPI.getAll();
                const mapping = categories.reduce((acc, category) => {
                    acc[category._id] = category.name;
                    return acc;
                }, {});
                setCategoryMap(mapping);
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        }
        fetchCategories();
    }, []);

    useEffect(() => {
        async function fetchBrands() {
            try {
                const response = await brandAPI.getAll(1, 1000, '', false)
                const brands = response.results || []
                const mapping = brands.reduce((acc, brand) => {
                    acc[brand._id] = brand.name
                    return acc
                }, {})
                setBrandMap(mapping)
            } catch (error) {
                console.error('Error fetching brands:', error)
            }
        }
        fetchBrands()
    }, []);

    // Add useEffect to fetch system settings
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const settings = await settingsAPI.get();
                setSystemSettings(settings);
            } catch (error) {
                console.error('Error fetching system settings:', error);
                // Keep default value if fetch fails
            }
        };
        fetchSettings();
    }, []);

    // Function to update URL with current filter states
    const updateUrlWithFilters = () => {
        const newSearchParams = new URLSearchParams();
        
        // Only add params that have values
        if (searchTerm) newSearchParams.set('search', searchTerm);
        if (currentPage > 1) newSearchParams.set('page', currentPage.toString());
        if (selectedVendor) newSearchParams.set('vendor', selectedVendor);
        if (selectedManufacturer) newSearchParams.set('manufacturer', selectedManufacturer);
        if (selectedBrand) newSearchParams.set('brand', selectedBrand);
        if (selectedCategories.length > 0) newSearchParams.set('categories', selectedCategories.join(','));
        if (bbDateRange.startDate) newSearchParams.set('bbStartDate', bbDateRange.startDate.toISOString().split('T')[0]);
        if (bbDateRange.endDate) newSearchParams.set('bbEndDate', bbDateRange.endDate.toISOString().split('T')[0]);
        if (selectedWarehouse) newSearchParams.set('warehouse', selectedWarehouse);
        if (selectedLocation) newSearchParams.set('location', selectedLocation);
        if (selectedInTransit) newSearchParams.set('inTransit', selectedInTransit);
        if (selectedUom) newSearchParams.set('uom', selectedUom);
        if (pageSize !== PaginationComponent.getCurrentPageSize()) newSearchParams.set('pageSize', pageSize.toString());
        
        // Replace the current URL with the new one including filters
        // Use replace instead of push to avoid adding to browser history
        navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
    };

    // Use this effect to synchronize URL with filter changes
    useEffect(() => {
        updateUrlWithFilters();
    }, [
        searchTerm,
        currentPage,
        selectedVendor,
        selectedManufacturer,
        selectedBrand,
        selectedCategories,
        selectedWarehouse,
        selectedLocation,
        selectedInTransit,
        selectedUom,
        pageSize,
        // Don't include bbDateRange as it triggers with the Set button
    ]);

    // Keep searchInputValue in sync with searchTerm when it changes from external sources
    useEffect(() => {
        setSearchInputValue(searchTerm);
    }, [searchTerm]);

    // Check for returnFilters when the component mounts
    useEffect(() => {
        // If we have returnFilters in the location state, restore them
        if (location.state?.returnFilters) {
            // Navigate to the same path but with the saved filters from state
            navigate(`${location.pathname}${location.state.returnFilters}`, { 
                replace: true,  // Replace current history entry
                state: {}       // Clear the state to avoid infinite loop
            });
        }
        
        // Check if we're returning from an action (indicated by refresh parameter)
        const urlParams = new URLSearchParams(location.search);
        if (urlParams.get('refresh')) {
            // Remove the refresh parameter and trigger data refresh
            urlParams.delete('refresh');
            const newSearch = urlParams.toString();
            const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
            
            // Update URL without refresh parameter and trigger data fetch
            navigate(newUrl, { replace: true });
            setShouldFetchData(prev => prev + 1);
        }
    }, []);  // Only run on mount

    const handleVendorFilterChange = (selectedOption) => {
        setSelectedVendor(selectedOption ? selectedOption.value : '');
        setCurrentPage(1);
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const toggleColumnVisibility = (column) => {
        if (column === 'in_transit' && !systemSettings.enable_warehouse_transit) {
            return; // Don't allow toggling the transit column when warehouse transit is disabled
        }
        setColumnVisibility(prevState => ({
            ...prevState,
            [column]: !prevState[column]
        }));
    };

    const handleClearSearch = () => {
        setSearchInputValue('');
        setSearchTerm('');
        setCurrentPage(1);

        // Clear any existing timeout
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
    };

    const handleSearchInputChange = (e) => {
        const value = e.target.value;

        // Update the input value immediately for responsive UI
        setSearchInputValue(value);

        // Clear any existing timeout
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        // Set a new timeout
        debounceTimeoutRef.current = setTimeout(() => {
            setSearchTerm(value);
            setCurrentPage(1);
            // URL will be updated via the useEffect
        }, 500); // Increased to 500ms for better performance
    };

    // Apply GTIN as search filter
    const handleFilterByGtin = (gtin) => {
        if (!gtin) return;

        setSearchInputValue(gtin);
        setSearchTerm(gtin);
        setCurrentPage(1);
    };

    const handleInTransitFilterChange = (inTransit) => {
        setSelectedInTransit(inTransit);
        setCurrentPage(1);
    };

    const handleCategorySelection = (e, categoryId) => {
        if (e.target.checked) {
            setSelectedCategories(prev => [...prev, categoryId]);
        } else {
            setSelectedCategories(prev => prev.filter(id => id !== categoryId));
        }
        setCurrentPage(1);
    };

    const handleWarehouseFilterChange = (selectedOption) => {
        setSelectedWarehouse(selectedOption ? selectedOption.value : '');
        setSelectedLocation(''); // Reset location when warehouse changes
        setCurrentPage(1);
    };

    const handleLocationFilterChange = (selectedOption) => {
        setSelectedLocation(selectedOption ? selectedOption.value : '');
        setCurrentPage(1);
    };

    const handleManufacturerFilterChange = (selectedOption) => {
        setSelectedManufacturer(selectedOption ? selectedOption.value : '');
        setCurrentPage(1);
    };

    const formatBrandOptions = () => {
        return Object.entries(brandMap).map(([id, name]) => ({
            value: id,
            label: name
        }));
    };

    const handleBrandFilterChange = (selectedOption) => {
        setSelectedBrand(selectedOption ? selectedOption.value : '');
        setCurrentPage(1);
    };

    // Modify the bb date range "Set" button handler to also update the URL
    const handleSetBbDateRange = (e) => {
        e.target.closest('.dropdown').querySelector('.dropdown-toggle').click();
        setShouldFetchData(prev => prev + 1);
        setDisplayedDateRange(bbDateRange);
        
        // Update URL with new date range
        updateUrlWithFilters();
    };

    const handleGenerateReport = async (fileType) => {
        try {
            // Show loading state
            setLoading(true);
            
            // Convert selectedCategories to a query string format
            const selectedCategoriesString = selectedCategories.map(id => `${id}`).join(',');
            
            const success = await inventoryItemAPI.generateInventoryReport(
                searchTerm,
                selectedVendor,
                selectedManufacturer,
                selectedCategoriesString,
                bbDateRange.startDate?.toISOString().split('T')[0],
                bbDateRange.endDate?.toISOString().split('T')[0],
                selectedWarehouse,
                selectedLocation,
                selectedInTransit,
                fileType
            );

            if (!success) {
                alert('Failed to generate report.');
            }
        } catch (error) {
            console.error('Error generating report:', error);
            alert('Failed to generate report. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Handle sorting
    const handleSort = (column, direction) => {
        setSortColumn(column);
        setSortDirection(direction);
        // Trigger data refetch with new sorting
        setShouldFetchData(prev => prev + 1);
    };

    const displayedInventoryItems = inventoryItems;

    const formatVendorOptions = () => {
        return Object.entries(vendorMap).map(([id, name]) => ({
            value: id,
            label: name
        }));
    };

    const formatWarehouseOptions = () => {
        return Object.entries(warehouseMap).map(([id, name]) => ({
            value: id,
            label: name
        }));
    };

    const formatLocationOptions = () => {
        return Object.entries(locationMap).map(([id, name]) => ({
            value: id,
            label: name
        }));
    };

    // Function to navigate to a new route while preserving the filter state
    const navigateWithFilterState = (path) => {
        navigate(path, {
            state: {
                returnFilters: location.search
            }
        });
    };

    // Functions for item selection and bulk actions
    const handleRowSelect = (row, isSelected) => {
        if (isSelected) {
            // Add the row to selected rows with default quantity = available quantity
            // Also store warehouse information to prevent issues when page changes
            const selectedItem = { 
                id: row._id, 
                quantity: row.quantity,
                // Extract warehouse ID from the location ID since it's not directly available
                warehouse_id: selectedWarehouse || row.location?.warehouse_id,
                warehouse_name: row.warehouse_name
            };
            
            setSelectedRows(prev => [...prev, selectedItem]);
        } else {
            // Remove the row from selected rows
            setSelectedRows(prev => prev.filter(item => item.id !== row._id));
        }
    };

    const handleSelectAll = (isSelected) => {
        setSelectAll(isSelected);
        if (isSelected) {
            // Select all visible rows that are not in transit
            // Store warehouse information for each selected item
            const allSelectableRows = inventoryItems
                .filter(item => !item.transit)
                .map(item => ({ 
                    id: item._id, 
                    quantity: item.quantity,
                    warehouse_id: item.warehouse_id,
                    warehouse_name: item.warehouse_name
                }));
            setSelectedRows(allSelectableRows);
        } else {
            // Deselect all rows
            setSelectedRows([]);
        }
    };

    const isRowSelected = (rowId) => {
        return selectedRows.some(item => item.id === rowId);
    };


    // Add effect to update column visibility when system settings change
    useEffect(() => {
        if (!systemSettings.enable_warehouse_transit) {
            setColumnVisibility(prev => ({
                ...prev,
                in_transit: false
            }));
        }
    }, [systemSettings.enable_warehouse_transit]);


    return (
        <Container fluid className="px-4 py-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1>Inventory Items</h1>
                </Col>
                <Col>
                    {createColumnToggleDropdown(columnVisibility, columnLabels, toggleColumnVisibility)}
                </Col>
                <Col md={2}>
                    <Dropdown>
                        <Dropdown.Toggle variant="success" id="report-dropdown">
                            Generate Report
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleGenerateReport('csv')}>
                                <i className="material-icons">description</i> Export as CSV
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => handleGenerateReport('pdf')}>
                                <i className="material-icons">picture_as_pdf</i> Export as PDF
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </Col>
                <Col md={4}>
                    <SearchInputWithClear
                        value={searchInputValue}
                        onChange={handleSearchInputChange}
                        onClear={handleClearSearch}
                        placeholder="Search by any field"
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Col>
            </Row>

            {/* Bulk Actions Toolbar */}
            {selectedRows.length > 0 && (
                <Row className="mb-3">
                    <Col>
                        <div className="d-flex align-items-center bg-body-secondary p-2 rounded">
                            <BulkOperationsToolbar 
                                selectedItems={selectedRows}
                                inventoryItems={inventoryItems}
                                warehouseMap={warehouseMap}
                                onSelectionClear={() => {
                                    setSelectedRows([]);
                                    setSelectAll(false);
                                }}
                                onRefreshData={() => setShouldFetchData(prev => prev + 1)}
                            />
                            {/* Show warning only if none of the selected items are in the current view */}
                            {selectedRows.length > 0 && inventoryItems.every(item => !selectedRows.some(row => row.id === item._id)) && (
                                <span className="text-warning me-3 small">
                                    <i className="material-icons small align-text-bottom">filter_alt</i> Selected items are not visible with current filters
                                </span>
                            )}
                        </div>
                    </Col>
                </Row>
            )}

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : (
                <>
                <StickyScrollbar>
                    <ResizableTable
                        tableId="inventory_items_table"
                        enableSorting={true}
                        onSort={handleSort}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        columns={[
                            ...(columnVisibility.selection ? [{
                                id: 'selection',
                                header: () => (
                                    <Form.Check
                                        type="checkbox"
                                        checked={selectAll}
                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                        label=""
                                    />
                                ),
                                cell: (row) => (
                                    <Form.Check
                                        type="checkbox"
                                        checked={isRowSelected(row._id)}
                                        onChange={(e) => handleRowSelect(row, e.target.checked)}
                                        disabled={row.transit} // Can't select items in transit
                                        label=""
                                    />
                                )
                            }] : []),
                            {
                                id: 'actions',
                                header: 'Actions',
                                cell: (row) => (
                                    <Dropdown>
                                        <Dropdown.Toggle variant="primary" size="sm">
                                            Actions
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            {row.transit ? (
                                                hasPermission('inventory.receive') && (
                                                    <Dropdown.Item
                                                        onClick={() => navigateWithFilterState(`/transfers/${row.transit}/receive`)}
                                                    >
                                                        <i className="material-icons">download</i> Receive Transfer
                                                    </Dropdown.Item>
                                                )
                                            ) : (
                                                <>
                                                    {hasPermission('inventory.receive') && (
                                                        <Dropdown.Item
                                                            disabled={!!row.in_production}
                                                            title={row.in_production ? "Item is allocated to an active production LOT" : undefined}
                                                            onClick={() => navigateWithFilterState(`/inventory/${row._id}/amend`)}
                                                        >
                                                            <i className="material-icons">edit</i> Edit
                                                        </Dropdown.Item>
                                                    )}
                                                    {hasPermission('inventory.move') && (
                                                        <Dropdown.Item
                                                            disabled={!!row.in_production}
                                                            title={row.in_production ? "Item is allocated to an active production LOT" : undefined}
                                                            onClick={() => navigateWithFilterState(`/inventory/${row._id}/move`)}
                                                        >
                                                            <i className="material-icons">trolley</i> Move
                                                        </Dropdown.Item>
                                                    )}
                                                    {systemSettings.enable_warehouse_transit && hasPermission('inventory.move') && (
                                                        <Dropdown.Item
                                                            disabled={!!row.in_production}
                                                            title={row.in_production ? "Item is allocated to an active production LOT" : undefined}
                                                            onClick={() => navigateWithFilterState(`/inventory/${row._id}/transfer`)}
                                                        >
                                                            <i className="material-icons">local_shipping</i> Transfer
                                                        </Dropdown.Item>
                                                    )}
                                                    {hasPermission('inventory.dispose') && (
                                                        <Dropdown.Item
                                                            disabled={!!row.in_production}
                                                            title={row.in_production ? "Item is allocated to an active production LOT" : undefined}
                                                            onClick={() => navigateWithFilterState(`/inventory/${row._id}/dispose`)}
                                                        >
                                                            <i className="material-icons">delete</i> Dispose
                                                        </Dropdown.Item>
                                                    )}
                                                    {hasPermission('issued.return') && (
                                                        <Dropdown.Item
                                                            disabled={!!row.in_production}
                                                            title={row.in_production ? "Item is allocated to an active production LOT" : undefined}
                                                            onClick={() => navigateWithFilterState(`/inventory/${row._id}/return`)}
                                                        >
                                                            <i className="material-icons">keyboard_return</i> Return
                                                        </Dropdown.Item>
                                                    )}
                                                    {hasPermission('inventory.issue') && (
                                                        <Dropdown.Item
                                                            disabled={!!row.in_production}
                                                            title={row.in_production ? "Item is allocated to an active production LOT" : undefined}
                                                            onClick={() => navigateWithFilterState(`/inventory/${row._id}/issue`)}
                                                        >
                                                            <i className="material-icons">sell</i> Issue
                                                        </Dropdown.Item>
                                                    )}
                                                </>
                                            )}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                )
                            },
                            ...(columnVisibility.inventory_id ? [{
                                id: 'inventory_id',
                                header: 'Inventory ID',
                                cell: (row) => (
                                    <div className="d-flex flex-column">
                                        {row.transit && (
                                            <small className="text-info">In Transit</small>
                                        )}
                                        <span>{highlightText(row._id, searchTerm)}</span>
                                    </div>
                                )
                            }] : []),
                            ...(columnVisibility.stock_item_id ? [{
                                id: 'stock_item_id',
                                header: 'Stock Item ID',
                                cell: (row) => highlightText(row.stock_item._id, searchTerm)
                            }] : []),
                            ...(columnVisibility.sku ? [{
                                id: 'sku',
                                header: 'SKU',
                                sortable: true,
                                cell: (row) => highlightText(row.stock_item.sku, searchTerm)
                            }] : []),
                            ...(columnVisibility.image ? [{
                                id: 'image',
                                header: 'Image',
                                cell: (row) => row.stock_item.image ? (
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={
                                            <Tooltip className="stock-item-tooltip">
                                                <StockItemInfo stockItem={row.stock_item} />
                                            </Tooltip>
                                        }
                                    >
                                        <img 
                                            src={row.stock_item.image} 
                                            alt="Stock Item Thumbnail"
                                            style={{
                                                width: '50px',
                                                height: '50px',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </OverlayTrigger>
                                ) : null
                            }] : []),
                            ...(columnVisibility.gtin ? [{
                                id: 'gtin',
                                header: 'GTIN',
                                cell: (row) => (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <CopyableText value={row.stock_item.gtin} toastLabel="GTIN" />
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={<Tooltip>Filter by this GTIN</Tooltip>}
                                        >
                                            <span
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => handleFilterByGtin(row.stock_item.gtin)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        handleFilterByGtin(row.stock_item.gtin);
                                                    }
                                                }}
                                                style={{
                                                    cursor: 'pointer',
                                                    color: '#6c757d',
                                                    fontSize: '16px',
                                                    transition: 'color 0.2s',
                                                }}
                                                onMouseEnter={(e) => e.target.style.color = '#0d6efd'}
                                                onMouseLeave={(e) => e.target.style.color = '#6c757d'}
                                                className="material-icons"
                                                aria-label={`Filter by GTIN ${row.stock_item.gtin}`}
                                            >
                                                filter_alt
                                            </span>
                                        </OverlayTrigger>
                                    </div>
                                )
                            }] : []),
                            ...(columnVisibility.upc ? [{
                                id: 'upc',
                                header: 'UPC',
                                cell: (row) => highlightText(row.stock_item.upc, searchTerm)
                            }] : []),
                            ...(columnVisibility.description ? [{
                                id: 'description',
                                header: 'Description',
                                sortable: true,
                                cell: (row) => {
                                    const expStatus = getExpirationStatus(row.bb_date);
                                    return (
                                        <>
                                            {highlightText(row.stock_item.description, searchTerm)}
                                            {expStatus === 'expired' && (
                                                <Badge bg="danger" className="ms-2">Expired</Badge>
                                            )}
                                            {expStatus === 'expiring_soon' && (
                                                <Badge bg="warning" text="dark" className="ms-2">Expiring Soon</Badge>
                                            )}
                                            {row.transit && (
                                                <Badge bg="info" className="ms-2">In Transit</Badge>
                                            )}
                                            {row.in_production && (
                                                <Badge bg="warning" text="dark" className="ms-2">In Production</Badge>
                                            )}
                                        </>
                                    );
                                }
                            }] : []),
                            ...(columnVisibility.lot ? [{
                                id: 'lot',
                                header: 'LOT',
                                sortable: true,
                                cell: (row) => highlightText(row.lot, searchTerm)
                            }] : []),
                            ...(columnVisibility.notes ? [{
                                id: 'notes',
                                header: 'Notes',
                                cell: (row) => highlightText(row.notes, searchTerm)
                            }] : []),
                            ...(columnVisibility.bbDate ? [{
                                id: 'bbDate',
                                header: () => (
                                        <Dropdown>
                                            <Dropdown.Toggle 
                                                variant="light" 
                                                id="bb-date-filter-dropdown"
                                                style={{
                                                    fontWeight: 'bold',
                                                    padding: '0rem 0.5rem 0.35rem',
                                                    height: '25px'
                                                }}
                                            >
                                                {displayedDateRange.startDate || displayedDateRange.endDate ? 
                                                    `BB: ${displayedDateRange.startDate?.toLocaleDateString() || 'Any'} - ${displayedDateRange.endDate?.toLocaleDateString() || 'Any'}` : 
                                                    'Best Before'}
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu style={{ padding: '10px', minWidth: '450px' }}>
                                                <div 
                                                    onClick={e => e.stopPropagation()}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.stopPropagation();
                                                        }
                                                    }}
                                                    role="button"
                                                    tabIndex={0}
                                                >
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <small>Start:</small>
                                                            <DatePicker
                                                                selected={bbDateRange.startDate}
                                                                onChange={date => {
                                                                    setBbDateRange(prev => ({ ...prev, startDate: date }));
                                                                }}
                                                                selectsStart
                                                                startDate={bbDateRange.startDate}
                                                                endDate={bbDateRange.endDate}
                                                                className="form-control form-control-sm"
                                                                placeholderText="Select start date"
                                                                isClearable
                                                                shouldCloseOnSelect={true}
                                                                showMonthDropdown
                                                                showYearDropdown
                                                                scrollableYearDropdown
                                                                yearDropdownItemNumber={5}  // Shows 10 years at a time
                                                                dropdownMode="select"  // Makes dropdowns use select elements
                                                            />
                                                        </div>
                                                        <div style={{ width: '10px' }}></div>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <small>End:</small>
                                                            <DatePicker
                                                                selected={bbDateRange.endDate}
                                                                onChange={date => {
                                                                    setBbDateRange(prev => ({ ...prev, endDate: date }));
                                                                }}
                                                                selectsEnd
                                                                startDate={bbDateRange.startDate}
                                                                endDate={bbDateRange.endDate}
                                                                minDate={bbDateRange.startDate}
                                                                className="form-control form-control-sm"
                                                                placeholderText="Select end date"
                                                                isClearable
                                                                shouldCloseOnSelect={true}
                                                                showMonthDropdown
                                                                showYearDropdown
                                                                scrollableYearDropdown
                                                                yearDropdownItemNumber={5}  // Shows 10 years at a time
                                                                dropdownMode="select"  // Makes dropdowns use select elements
                                                            />
                                                        </div>
                                                    </div>
                                                    <div style={{ height: '10px' }}></div>
                                                    <div className="mt-2">
                                                        <Dropdown>
                                                            <Dropdown.Toggle 
                                                                variant="outline-secondary" 
                                                                size="sm"
                                                                id="quick-date-select"
                                                            >
                                                                Quick Select
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu>
                                                                <Dropdown.Item 
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        const today = new Date();
                                                                        setBbDateRange({
                                                                            startDate: null,
                                                                            endDate: today
                                                                        });
                                                                        e.target.closest('.dropdown').querySelector('.dropdown-toggle').click();
                                                                    }}
                                                                >
                                                                    Already Expired
                                                                </Dropdown.Item>
                                                                <Dropdown.Item 
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        const today = new Date();
                                                                        const threeMonths = new Date(today);
                                                                        threeMonths.setMonth(today.getMonth() + 3);
                                                                        setBbDateRange({
                                                                            startDate: today,
                                                                            endDate: threeMonths
                                                                        });
                                                                        e.target.closest('.dropdown').querySelector('.dropdown-toggle').click();
                                                                    }}
                                                                >
                                                                    Next 3 Months
                                                                </Dropdown.Item>
                                                                <Dropdown.Item 
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        const today = new Date();
                                                                        const sixMonths = new Date(today);
                                                                        sixMonths.setMonth(today.getMonth() + 6);
                                                                        setBbDateRange({
                                                                            startDate: today,
                                                                            endDate: sixMonths
                                                                        });
                                                                        e.target.closest('.dropdown').querySelector('.dropdown-toggle').click();
                                                                    }}
                                                                >
                                                                    Next 6 Months
                                                                </Dropdown.Item>
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </div>
                                                    <div className="d-flex gap-2 mt-2 justify-content-between">
                                                        <div className="d-flex gap-2">
                                                            <Button 
                                                                variant="outline-secondary" 
                                                                size="sm"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setBbDateRange({ startDate: null, endDate: null });
                                                                }}
                                                            >
                                                                Clear Dates
                                                            </Button>
                            
                                                            <Button 
                                                                variant="outline-secondary" 
                                                                size="sm"
                                                                onClick={(e) => {
                                                                    e.target.closest('.dropdown').querySelector('.dropdown-toggle').click();
                                                                }}
                                                            >
                                                                Cancel
                                                            </Button>
                                                        </div>
                                                        <Button 
                                                            variant="primary" 
                                                            size="sm"
                                                            onClick={handleSetBbDateRange}
                                                        >
                                                            Set
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                ),
                                cell: (row) => formatDateOnly(row.bb_date)
                            }] : []),
                            ...(columnVisibility.receivedDate ? [{
                                id: 'receivedDate',
                                header: 'Received Date',
                                cell: (row) => formatDate(row.received_datetime)
                            }] : []),
                            ...(columnVisibility.uom ? [{
                                id: 'uom',
                                header: () => createFilterDropdown({
                                    selectedValue: selectedUom,
                                    handleSelect: (value) => { setSelectedUom(value); setCurrentPage(1); },
                                    items: UOM_MAP,
                                    defaultLabel: 'UOM',
                                    allItemsLabel: 'All'
                                }),
                                cell: (row) => UOM_MAP[row.stock_item.uom] || row.stock_item.uom || ''
                            }] : []),
                            ...(columnVisibility.warehouse ? [{
                                id: 'warehouse',
                                header: () => (
                                    <div style={{ minWidth: '200px' }} className="compact-select">
                                        <Select
                                            value={selectedWarehouse ? { value: selectedWarehouse, label: warehouseMap[selectedWarehouse] } : null}
                                            onChange={handleWarehouseFilterChange}
                                            options={formatWarehouseOptions()}
                                            isSearchable
                                            isClearable
                                            placeholder="Warehouse"
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                ),
                                cell: (row) => (
                                    <div className="d-flex flex-column">
                                        {row.transit ? (
                                            <span className="text-info">In Transit</span>
                                        ) : (
                                            <span>{row.warehouse_name}</span>
                                        )}
                                    </div>
                                )
                            }] : []),
                            ...(columnVisibility.location ? [{
                                id: 'location',
                                header: () => (
                                    <div style={{ minWidth: '200px' }} className="compact-select">
                                        <Select
                                            value={selectedLocation ? { value: selectedLocation, label: locationMap[selectedLocation] } : null}
                                            onChange={handleLocationFilterChange}
                                            options={formatLocationOptions()}
                                            isSearchable
                                            isClearable
                                            placeholder="Location"
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            isDisabled={!selectedWarehouse}
                                        />
                                    </div>
                                ),
                                cell: (row) => (
                                    <div className="d-flex flex-column">
                                        {row.transit ? (
                                            <span className="text-info">In Transit</span>
                                        ) : (
                                            <span>{row.location_name}</span>
                                        )}
                                    </div>
                                )
                            }] : []),
                            ...(columnVisibility.categories ? [{
                                id: 'categories',
                                header: () => (
                                    <Dropdown>
                                        <Dropdown.Toggle 
                                            variant="light" 
                                            id="category-filter-dropdown"
                                            style={{
                                                fontWeight: 'bold',
                                                padding: '0rem 0.5rem 0.35rem',
                                                height: '25px'
                                            }}
                                        >
                                            {selectedCategories.length > 0 ? selectedCategories.map(id => categoryMap[id]).join(', ') : 'Categories'}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            <Dropdown.Item as="button" onClick={() => setSelectedCategories([])}>
                                                <Form.Check 
                                                    type="checkbox"
                                                    label="All Categories"
                                                    checked={selectedCategories.length === 0}
                                                    onChange={() => setSelectedCategories([])}
                                                />
                                            </Dropdown.Item>
                                            {Object.entries(categoryMap).map(([id, name]) => (
                                                <Dropdown.Item as="button" key={id}>
                                                    <Form.Check 
                                                        type="checkbox"
                                                        label={name}
                                                        checked={selectedCategories.includes(id)}
                                                        onChange={(e) => handleCategorySelection(e, id)}
                                                    />
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                ),
                                cell: (row) => row.stock_item.categories?.map(categoryId => categoryMap[categoryId]).join(', ')
                            }] : []),
                            ...(columnVisibility.vendor ? [{
                                id: 'vendor',
                                header: () => (
                                    <div style={{ minWidth: '200px' }} className="compact-select">
                                        <Select
                                            value={selectedVendor ? { value: selectedVendor, label: vendorMap[selectedVendor] } : null}
                                            onChange={handleVendorFilterChange}
                                            options={formatVendorOptions()}
                                            isSearchable
                                            isClearable
                                            placeholder="Vendor"
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                ),
                                cell: (row) => vendorMap[row.vendor] || row.vendor || 'N/A'
                            }] : []),
                            ...(columnVisibility.manufacturer ? [{
                                id: 'manufacturer',
                                header: () => (
                                    <div style={{ minWidth: '200px' }} className="compact-select">
                                        <Select
                                            value={selectedManufacturer ? { value: selectedManufacturer, label: vendorMap[selectedManufacturer] } : null}
                                            onChange={handleManufacturerFilterChange}
                                            options={formatVendorOptions()}
                                            isSearchable
                                            isClearable
                                            placeholder="Manufacturer"
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                ),
                                cell: (row) => vendorMap[row.stock_item.manufacturer] || row.stock_item.manufacturer || 'N/A'
                            }] : []),
                            ...(columnVisibility.brand ? [{
                                id: 'brand',
                                header: () => (
                                    <div style={{ minWidth: '200px' }} className="compact-select">
                                        <Select
                                            value={selectedBrand ? { value: selectedBrand, label: brandMap[selectedBrand] } : null}
                                            onChange={handleBrandFilterChange}
                                            options={formatBrandOptions()}
                                            isSearchable
                                            isClearable
                                            placeholder="Brand"
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                ),
                                cell: (row) => row.stock_item.brand_name || ''
                            }] : []),
                            ...(columnVisibility.cost ? [{
                                id: 'cost',
                                header: 'Cost',
                                cell: (row) => row.cost ? formatUnitCost(row.cost) : ''
                            }] : []),
                            ...(columnVisibility.totalCost ? [{
                                id: 'totalCost',
                                header: 'Total Cost',
                                cell: (row) => row.total_cost ? formatCurrency(row.total_cost) : ''
                            }] : []),
                            ...(columnVisibility.itemSize ? [{
                                id: 'itemSize',
                                header: 'Item Size',
                                cell: (row) => row.stock_item.item_size ? `${row.stock_item.item_size} ${row.stock_item.item_size_units}` : ''
                            }] : []),
                            ...(columnVisibility.casePack ? [{
                                id: 'casePack',
                                header: 'Case Pack',
                                cell: (row) => row.stock_item.case_pack
                            }] : []),
                            ...(columnVisibility.netCaseWeight ? [{
                                id: 'netCaseWeight',
                                header: 'Net Case Weight (lbs)',
                                cell: (row) => calculateNetCaseWeight(row.stock_item.item_size, row.stock_item.item_size_units, row.stock_item.case_pack)
                            }] : []),
                            ...(columnVisibility.packageWeight ? [{
                                id: 'packageWeight',
                                header: 'Package Weight',
                                cell: (row) => row.stock_item.package_weight && `${row.stock_item.package_weight}${row.stock_item.package_weight_units ? ` ${row.stock_item.package_weight_units}` : ' lbs'}`
                            }] : []),
                            ...(columnVisibility.grossCaseWeight ? [{
                                id: 'grossCaseWeight',
                                header: 'Gross Case Weight (lbs)',
                                cell: (row) => {
                                    const weight = calculateGrossCaseWeight(row.stock_item.item_size, 
                                        row.stock_item.item_size_units, row.stock_item.case_pack, row.stock_item.package_weight, row.stock_item.package_weight_units);
                                    return isNaN(weight) ? '' : weight;
                                }
                            }] : []),
                            ...(columnVisibility.ti ? [{
                                id: 'ti',
                                header: 'TI',
                                cell: (row) => row.stock_item.ti
                            }] : []),
                            ...(columnVisibility.hi ? [{
                                id: 'hi',
                                header: 'HI',
                                cell: (row) => row.stock_item.hi
                            }] : []),
                            ...(columnVisibility.countryOfOrigin ? [{
                                id: 'countryOfOrigin',
                                header: 'Country of Origin',
                                cell: (row) => row.stock_item.country_of_origin && (
                                    <>
                                        {getCountryFlag(row.stock_item.country_of_origin)}{' '}
                                        {row.stock_item.country_of_origin}{' '}
                                    </>
                                )
                            }] : []),
                            ...(columnVisibility.length ? [{
                                id: 'length',
                                header: 'L(inch)',
                                cell: (row) => row.stock_item.dimensions_l
                            }] : []),
                            ...(columnVisibility.width ? [{
                                id: 'width',
                                header: 'W(inch)',
                                cell: (row) => row.stock_item.dimensions_w
                            }] : []),
                            ...(columnVisibility.height ? [{
                                id: 'height',
                                header: 'H(inch)',
                                cell: (row) => row.stock_item.dimensions_h
                            }] : []),
                            ...(columnVisibility.volume ? [{
                                id: 'volume',
                                header: 'Volume',
                                cell: (row) => calculateVolumeCubicFeet(row.stock_item.dimensions_l, row.stock_item.dimensions_w, row.stock_item.dimensions_h)
                            }] : []),
                            ...(columnVisibility.quantity ? [{
                                id: 'quantity',
                                sortable: true,
                                header: () => (
                                    <>
                                        Quantity
                                        <div className="d-flex flex-column gap-1 mt-1">
                                            {totalQuantity > 0 && (
                                                <div 
                                                    className="badge bg-success" 
                                                    style={{
                                                        fontSize: '0.8em',
                                                        padding: '4px 8px',
                                                        borderRadius: '12px',
                                                        fontWeight: 'normal'
                                                    }}
                                                >
                                                    Items: {totalQuantity.toLocaleString()}
                                                </div>
                                            )}
                                            {totalCases > 0 && (
                                                <div 
                                                    className="badge bg-info" 
                                                    style={{
                                                        fontSize: '0.8em',
                                                        padding: '4px 8px',
                                                        borderRadius: '12px',
                                                        fontWeight: 'normal'
                                                    }}
                                                >
                                                    Cases: {totalCases.toLocaleString()}
                                                </div>
                                            )}
                                            {totalWeightLbs > 0 && (
                                                <div 
                                                    className="badge bg-warning" 
                                                    style={{
                                                        fontSize: '0.8em',
                                                        padding: '4px 8px',
                                                        borderRadius: '12px',
                                                        fontWeight: 'normal'
                                                    }}
                                                >
                                                    Weight: {totalWeightLbs.toLocaleString()} lbs
                                                </div>
                                            )}
                                            {totalPallets > 0 && (
                                                <div
                                                    className="badge bg-secondary"
                                                    style={{
                                                        fontSize: '0.8em',
                                                        padding: '4px 8px',
                                                        borderRadius: '12px',
                                                        fontWeight: 'normal'
                                                    }}
                                                >
                                                    Pallets: {totalPallets.toLocaleString()}
                                                </div>
                                            )}
                                            {totalFilm > 0 && (
                                                <div
                                                    className="badge bg-secondary"
                                                    style={{
                                                        fontSize: '0.8em',
                                                        padding: '4px 8px',
                                                        borderRadius: '12px',
                                                        fontWeight: 'normal'
                                                    }}
                                                >
                                                    Film: {totalFilm.toLocaleString()}
                                                </div>
                                            )}
                                            {totalClearFilm > 0 && (
                                                <div
                                                    className="badge bg-secondary"
                                                    style={{
                                                        fontSize: '0.8em',
                                                        padding: '4px 8px',
                                                        borderRadius: '12px',
                                                        fontWeight: 'normal'
                                                    }}
                                                >
                                                    Clear Film: {totalClearFilm.toLocaleString()}
                                                </div>
                                            )}
                                        </div>
                                    </>
                                ),
                                cell: (row) => formatQuantity(row.quantity)
                            }] : []),
                            ...(systemSettings.enable_warehouse_transit && columnVisibility.in_transit ? [{
                                id: 'in_transit',
                                header: () => (
                                         <Dropdown onSelect={handleInTransitFilterChange}>
                                            <Dropdown.Toggle 
                                                variant="light" 
                                                id="vendor-filter-dropdown"
                                                style={{
                                                    fontWeight: 'bold',
                                                    padding: '0rem 0.5rem 0.35rem',
                                                    height: '25px'
                                                }}
                                            >
                                                {(() => {
                                                    switch(selectedInTransit) {
                                                        case 'true': return 'In Transit';
                                                        case 'false': return 'Not in Transit';
                                                        default: return 'Transit';
                                                    }
                                                })()}
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item eventKey="">All</Dropdown.Item>
                                                <Dropdown.Item eventKey="true">In Transit</Dropdown.Item>
                                                <Dropdown.Item eventKey="false">Not In Transit</Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                ),
                                cell: (row) => row.transit ? <i className="material-icons">local_shipping</i> : ''
                            }] : [])
                        ]}
                        data={displayedInventoryItems}
                        initialColumnWidths={columnWidths}
                        getRowClassName={getRowClassName}
                        striped
                        hover
                    />
                </StickyScrollbar>
                <PaginationComponent
                    currentPage={currentPage}
                    totalItems={totalPages}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={setPageSize}
                />
                </>
            )}

        </Container>
    )
}

export default InventoryListScreen